"use client";

import { useState } from "react";
import { Strategy } from "@/stores/strategiesStore";
import { PerformanceChart } from "@/components/strategies/PerformanceChart";
import { ContractBadge } from "@/components/strategies/ContractBadge";
import { OfferedBy } from "@/components/strategies/OfferedBy";
import { getDemoPricing } from "@/utils/demoPricing";
import { MarketViewBadge } from "./MarketViewBadge";
import { OwnerBadge } from "./OwnerBadge";
import { RiskBadge } from "./RiskBadge";
import { StrategyMetrics } from "./StrategyMetrics";
import { UserAllocationBadge } from "./UserAllocationBadge";

interface StrategyCardProps {
  strategy: Strategy;
  onAllocate: (strategyId: string) => void;
  onManage: (strategyId: string) => void;
  isViewOnly?: boolean;
}

export const StrategyCard = ({
  strategy,
  onAllocate,
  onManage,
  isViewOnly = false,
}: StrategyCardProps) => {
  const hasAllocation = strategy.userAllocation !== null;
  const [showChart, setShowChart] = useState(false);
  const pricing = getDemoPricing(strategy.slug);

  return (
    <div className="flex h-full flex-col rounded-xl border border-slate-700 bg-slate-800 p-5 transition hover:border-slate-600">
      {/* Header — min-h reserves a consistent 2-line title block so cards
          with a 1-line name (e.g. "BS Atlas") and cards with a 2-line name
          (e.g. "Gamma Range ES") start their description at the same y. */}
      <div className="mb-3 flex min-h-14 items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <div className="flex shrink-0 items-center gap-2 pt-0.5">
            <MarketViewBadge view={strategy.marketView} />
            {strategy.ownerType === "third_party" && <OwnerBadge />}
          </div>
          <h3
            className="line-clamp-2 text-lg font-semibold text-white"
            title={strategy.name}
          >
            {strategy.name}
          </h3>
        </div>
        <RiskBadge level={strategy.riskLevel} />
      </div>

      {/* Description */}
      <p
        className="mb-3 line-clamp-2 text-sm text-slate-400"
        title={strategy.description ?? undefined}
      >
        {strategy.description}
      </p>

      {/* Contracts */}
      {strategy.supportedAssets?.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1">
          <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
            Contracts
          </span>
          {strategy.supportedAssets.map((asset) => (
            <ContractBadge key={asset} asset={asset} />
          ))}
        </div>
      )}

      {/* Per-unit pricing (demo display only, see @/utils/demoPricing) */}
      {pricing && (
        <div className="mb-4 flex items-center gap-1.5 whitespace-nowrap text-xs">
          <span className="font-medium uppercase tracking-wider text-slate-500">
            Per unit
          </span>
          <span className="font-semibold text-white">{pricing.unitEquity}</span>
          <span className="text-slate-600">·</span>
          <span className="text-slate-400">{pricing.tierLabel}</span>
        </div>
      )}

      {/* Metrics Grid */}
      <StrategyMetrics strategy={strategy} />

      {/* Performance Chart Toggle */}
      <button
        onClick={() => setShowChart(!showChart)}
        className="mt-3 text-xs font-medium text-slate-400 transition hover:text-blue-400"
      >
        {showChart ? "Hide Performance" : "View Performance"}
      </button>

      {showChart && (
        <>
          <PerformanceChart
            strategyId={strategy.id}
            strategyName={strategy.name}
            className="mt-3"
          />
          {strategy.ownerType === "third_party" && (
            <OfferedBy
              ownerName={strategy.ownerName}
              ownerLogoUrl={strategy.ownerLogoUrl}
              disclosureUrl={strategy.disclosureUrl}
              className="mt-3"
            />
          )}
        </>
      )}

      {/* User Allocation (if any) */}
      {hasAllocation && <UserAllocationBadge allocation={strategy.userAllocation!} />}

      {/* Actions — mt-auto pins this to the bottom of the flex column so
          Allocate buttons align across a row regardless of title/description
          length (SFWL demo card-alignment fix). */}
      {!isViewOnly && (
        <div className="mt-auto flex gap-2 pt-4">
          {hasAllocation ? (
            <>
              <button
                onClick={() => onManage(strategy.id)}
                className="flex-1 rounded-lg border border-slate-600 py-2 text-sm font-medium text-slate-300 transition hover:bg-slate-800 hover:text-white"
              >
                Manage
              </button>
              <button
                onClick={() => onAllocate(strategy.id)}
                className="flex-1 rounded-lg bg-blue-500 py-2 text-sm font-semibold text-white transition hover:bg-blue-600"
              >
                + Add More
              </button>
            </>
          ) : (
            <button
              onClick={() => onAllocate(strategy.id)}
              className="w-full rounded-lg bg-blue-500 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-600"
            >
              Allocate
            </button>
          )}
        </div>
      )}
    </div>
  );
};
